// The "Also show on the map" boxes, applied in the browser.
//
// The search page holds every context pin the rep may see, drawn or not (the
// server flags each one `context`), so a box never re-runs the search: it only
// changes which of them are drawn. Pure, so the rule reads in one place.

// Whether a feature is drawn under these ticked tones. A result (a candidate,
// a promoted account) is the answer to the search and no box governs it; a
// context pin is drawn while its tone's box is ticked. A feature with no
// `context` flag — the site page's single pin — is always drawn.
// `context_tone`: the box that drew a context pin whose tone has since changed
// in place (a saved site converted from its card) still governs it.
export function isDrawn(feature, shownTones) {
  return (
    !feature.context || shownTones.has(feature.context_tone || feature.tone)
  )
}

// The features drawn under these ticked tones, with one more rule: a saved
// site linked to an account gives way to that account's own pin (the user's
// call, 2026-10-13). The two stand on one building and say one thing, so
// while an account pin is drawn and placed, its linked saved sites are not —
// the rep sees the account and its card, never two pins on one spot. With the
// account's box unticked, or the account unplaced, the saved site is drawn as
// before, so a place never drops off the map. `keepId`: the pin the rep is
// inspecting stays until the card is closed, so linking it from its own card
// does not snatch the card away.
export function drawnFeatures(features, shownTones, { keepId = null } = {}) {
  const drawn = features.filter((feature) => isDrawn(feature, shownTones))
  const accounts = new Set(
    drawn
      .filter((f) => f.kind === "known" && f.account_id && f.lngLat)
      .map((f) => f.account_id),
  )
  return drawn.filter(
    (f) =>
      f.id === keepId ||
      f.kind !== "saved" ||
      !f.account_id ||
      !accounts.has(f.account_id),
  )
}

// A count said in the page's language. `forms` are the locale file's own
// plural forms for one key ({one:, other:}, or Polish's few/many, Arabic's
// zero/two…), so how a count reads stays the locale's decision; the category
// comes from the same CLDR rules rails-i18n applies on the server.
export function pluralise(forms, count, locale) {
  let category = "other"
  try {
    category = new Intl.PluralRules(locale || undefined).select(count)
  } catch {
    // An unknown locale tag: `other` is the form every locale has.
  }
  const template = forms?.[category] ?? forms?.other ?? ""
  return template.replaceAll("%{count}", String(count))
}
